import React, { useEffect, useState } from "react";
import {
  FaUser,
  FaPhone,
  FaCalendar,
  FaExclamationCircle,
  FaCheckCircle,
  FaClock,
  FaFilter,
  FaSearch,
  FaUserPlus,
  FaEye,
  FaEdit,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import IssueAssignModal from "../components/IssueAssignModal";
import IssueDetailModal from "../components/IssueDetailModal";
import IssueStatusUpdateModal from "../components/IssueStatusUpdateModal";

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Filters and Search
  const [filters, setFilters] = useState({
    status: "",
    assignedTo: "",
    issueType: "",
    role: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalIssues: 0,
    limit: 10,
  });

  // Modals
  const [assignModal, setAssignModal] = useState({ show: false, issue: null });
  const [detailModal, setDetailModal] = useState({ show: false, issue: null });
  const [statusModal, setStatusModal] = useState({ show: false, issue: null });

  // Fetch current user profile
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        console.log(
          "🚨 Fetching admin profile with token:",
          token ? "Token exists" : "No token"
        );

        const response = await axiosInstance.get(API.ADMIN.GET_ADMIN_PROFILE, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("🚨 Full API Response:", response.data);
        console.log("🚨 User Data:", response.data.data);
        console.log("🚨 User Role:", response.data.data?.role);
        console.log("🚨 User Role Slug:", response.data.data?.role?.slug);

        setCurrentUser(response.data.data);
      } catch (error) {
        console.error("🚨 Error fetching user profile:", error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch issues with filters and pagination
  const fetchIssues = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          queryParams.append(key, value);
        }
      });

      const response = await axiosInstance.get(
        `${API.ADMIN.BASE}/issues/all?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setIssues(response.data.data.issues || []);
      setPagination(response.data.data.pagination || pagination);
    } catch (error) {
      console.error("Error fetching issues:", error);
      toast.error("Failed to fetch issues");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available employees
  const fetchEmployees = async () => {
    console.log("fetchEmployees called, currentUser:", currentUser);

    if (!currentUser) {
      console.log("No currentUser, skipping employee fetch");
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");

      // Always use the general endpoint and filter on frontend for now
      // This ensures we get data even if specific endpoints don't exist
      const endpoint = `${API.ADMIN.BASE}/issues/employees/available`;

      console.log("Fetching from endpoint:", endpoint);

      const response = await axiosInstance.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("API Response:", response.data);

      let availableEmployees =
        response.data.data?.employees || response.data.employees || [];

      console.log("Raw employees before filtering:", availableEmployees);

      // Apply role-based filtering
      if (currentUser?.role?.slug === "senior") {
        console.log(`Filtering for senior user in city: ${currentUser.city}`);
        availableEmployees = availableEmployees.filter((emp) => {
          console.log(
            `Employee: ${emp.fullName}, Role: ${emp.role}, City: ${emp.city}`
          );
          return emp.role === "junior" && emp.city === currentUser.city;
        });
        console.log("Filtered employees for senior:", availableEmployees);
      } else if (
        currentUser?.role?.slug === "admin" ||
        currentUser?.role?.slug === "superadmin"
      ) {
        console.log("Admin/Superadmin user - showing all employees");
      }

      console.log(
        `Final employees count: ${availableEmployees.length} for role: ${
          currentUser?.role?.slug || currentUser?.role
        }`
      );
      setEmployees(availableEmployees);
    } catch (error) {
      console.error("Error fetching employees:", error);

      // Try alternative endpoint structure
      try {
        const token = localStorage.getItem("adminToken");
        const alternativeResponse = await axiosInstance.get(
          `${API.ADMIN.EMPLOYEES_LIST}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("Alternative API Response:", alternativeResponse.data);

        const allEmployees =
          alternativeResponse.data.data?.employees ||
          alternativeResponse.data.employees ||
          alternativeResponse.data ||
          [];

        let filteredEmployees = allEmployees;

        if (currentUser?.role?.slug === "senior") {
          filteredEmployees = allEmployees.filter(
            (emp) => emp.role === "junior" && emp.city === currentUser.city
          );
        }

        console.log("Alternative filtered employees:", filteredEmployees);
        setEmployees(filteredEmployees);
      } catch (alternativeError) {
        console.error(
          "Alternative employee fetch also failed:",
          alternativeError
        );
        setEmployees([]); // Set empty array as fallback
      }
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axiosInstance.get(
        `${API.ADMIN.BASE}/issues/statistics`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStatistics(response.data.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    }
  };

  useEffect(() => {
    fetchIssues();
    fetchStatistics();
    // Only fetch employees if currentUser is loaded (for proper filtering)
    if (currentUser) {
      fetchEmployees();
    }
  }, [filters, currentUser]);

  useEffect(() => {
    fetchIssues(pagination.currentPage);
  }, [pagination.currentPage]);

  // Fetch employees when currentUser changes
  useEffect(() => {
    if (currentUser) {
      fetchEmployees();
    }
  }, [currentUser]);

  // Handle issue assignment
  const handleAssignIssue = async (issueId, employeeId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axiosInstance.post(
        `${API.ADMIN.BASE}/issues/${issueId}/assign`,
        { assignToEmployeeId: employeeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Issue assigned successfully!");
      setAssignModal({ show: false, issue: null });
      fetchIssues(pagination.currentPage);
      fetchStatistics();
    } catch (error) {
      console.error("Error assigning issue:", error);
      toast.error(error.response?.data?.message || "Failed to assign issue");
    }
  };

  // Handle status update
  const handleStatusUpdate = async (issueId, statusData) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axiosInstance.put(
        `${API.ADMIN.BASE}/issues/${issueId}/status`,
        statusData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Issue status updated successfully!");
      setStatusModal({ show: false, issue: null });
      fetchIssues(pagination.currentPage);
      fetchStatistics();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "text-yellow-600 bg-yellow-100";
      case "In progress":
        return "text-blue-600 bg-blue-100";
      case "Resolved":
        return "text-green-600 bg-green-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  // Get priority color based on issue type
  const getPriorityColor = (issueType) => {
    const highPriority = ["Payment Problems", "Login Issues"];
    return highPriority.includes(issueType)
      ? "text-red-600"
      : "text-orange-600";
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Issue Management
        </h1>
        <p className="text-gray-600">
          Manage and assign customer issues to team members
        </p>

        {/* Role-specific information for Superadmin */}
        {currentUser?.role?.slug === "superadmin" && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaUser className="text-green-600 mr-2" />
                <div>
                  <p className="text-green-800 font-medium">Super Admin View</p>
                  <p className="text-green-600 text-sm">
                    You have full access to assign issues to any employee.
                    {employees.length === 0
                      ? " No employees are currently available."
                      : ` ${employees.length} employee(s) available for assignment.`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  console.log("Debug: Manual refresh clicked by superadmin");
                  fetchEmployees();
                }}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
              >
                Refresh Employees
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaExclamationCircle className="text-yellow-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Pending Issues</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {statistics.pendingIssues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaClock className="text-blue-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">
                  {statistics.inProgressIssues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaCheckCircle className="text-green-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Resolved</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.resolvedIssues}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <FaUser className="text-purple-500 text-2xl mr-3" />
              <div>
                <p className="text-gray-500 text-sm">Total Issues</p>
                <p className="text-2xl font-bold text-purple-600">
                  {statistics.totalIssues}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search issues..."
              className="pl-10 pr-4 py-2 border rounded-lg w-full"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="border rounded-lg px-3 py-2"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="In progress">In Progress</option>
            <option value="Resolved">Resolved</option>
          </select>

          {/* Issue Type Filter */}
          <select
            className="border rounded-lg px-3 py-2"
            value={filters.issueType}
            onChange={(e) => handleFilterChange("issueType", e.target.value)}
          >
            <option value="">All Types</option>
            <option value="Confirm Button Not Responding">Button Issues</option>
            <option value="Login Issues">Login Issues</option>
            <option value="Payment Problems">Payment Problems</option>
            <option value="Registration Issues">Registration Issues</option>
          </select>

          {/* Role Filter */}
          <select
            className="border rounded-lg px-3 py-2"
            value={filters.role}
            onChange={(e) => handleFilterChange("role", e.target.value)}
          >
            <option value="">All Roles</option>
            <option value="driver">Driver</option>
            <option value="user">User</option>
          </select>

          {/* Assigned To Filter */}
          <select
            className="border rounded-lg px-3 py-2"
            value={filters.assignedTo}
            onChange={(e) => handleFilterChange("assignedTo", e.target.value)}
          >
            <option value="">All Assignments</option>
            <option value="unassigned">Unassigned</option>
            {employees.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.fullName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Issues List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading issues...</span>
          </div>
        ) : issues.length === 0 ? (
          <div className="text-center py-12">
            <FaExclamationCircle className="text-gray-400 text-4xl mx-auto mb-4" />
            <p className="text-gray-500">
              No issues found matching your criteria
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {issues.map((issue) => (
                  <tr key={issue._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center">
                          <FaExclamationCircle
                            className={`${getPriorityColor(
                              issue.issueType
                            )} mr-2`}
                          />
                          <p className="text-sm font-medium text-gray-900">
                            {issue.title}
                          </p>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {issue.issueType}
                        </p>
                        <p
                          className="text-xs text-gray-400 truncate max-w-xs"
                          title={issue.message}
                        >
                          {issue.message}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FaPhone className="text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {issue.phone}
                          </p>
                          <p className="text-sm text-gray-500 capitalize">
                            {issue.role}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          issue.status
                        )}`}
                      >
                        {issue.status}
                      </span>
                      <div className="flex items-center mt-1 text-xs text-gray-500">
                        <FaCalendar className="mr-1" />
                        {new Date(issue.createdAt).toLocaleDateString()}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      {issue.assignedTo ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {issue.assignedTo.fullName}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">
                            {issue.assignedTo.role}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Unassigned
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setDetailModal({ show: true, issue })}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <FaEye />
                        </button>

                        {(currentUser?.role?.slug === "senior" ||
                          currentUser?.role?.slug === "admin" ||
                          currentUser?.role?.slug === "superadmin") && (
                          <button
                            onClick={() => {
                              console.log(
                                "Assign button clicked, refreshing employees..."
                              );
                              fetchEmployees(); // Refresh employees when opening modal
                              setAssignModal({ show: true, issue });
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Assign Issue"
                          >
                            <FaUserPlus />
                          </button>
                        )}

                        {(issue.assignedTo?._id === currentUser?._id ||
                          currentUser?.role?.slug === "admin" ||
                          currentUser?.role?.slug === "superadmin" ||
                          currentUser?.role?.slug === "senior") && (
                          <button
                            onClick={() =>
                              setStatusModal({ show: true, issue })
                            }
                            className="text-orange-600 hover:text-orange-800"
                            title="Update Status"
                          >
                            <FaEdit />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
              {Math.min(
                pagination.currentPage * pagination.limit,
                pagination.totalIssues
              )}{" "}
              of {pagination.totalIssues} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }))
                }
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {Array.from(
                { length: pagination.totalPages },
                (_, i) => i + 1
              ).map((page) => (
                <button
                  key={page}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, currentPage: page }))
                  }
                  className={`px-3 py-1 border rounded text-sm ${
                    page === pagination.currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : "hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }))
                }
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <IssueAssignModal
        show={assignModal.show}
        issue={assignModal.issue}
        employees={employees}
        onClose={() => setAssignModal({ show: false, issue: null })}
        onAssign={handleAssignIssue}
      />

      <IssueDetailModal
        show={detailModal.show}
        issue={detailModal.issue}
        onClose={() => setDetailModal({ show: false, issue: null })}
      />

      <IssueStatusUpdateModal
        show={statusModal.show}
        issue={statusModal.issue}
        onClose={() => setStatusModal({ show: false, issue: null })}
        onUpdate={handleStatusUpdate}
      />
    </div>
  );
};

export default Issues;
